import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDisplayDate } from "@/lib/utils";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Tag,
  MapPin,
  Car,
  Calendar,
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Activity,
  DollarSign,
  Users,
  Clock,
  Gift,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import DataTablePagination from "@/components/ui/data-table-pagination";
import {
  usePromotions,
  useDeletePromotion,
  useTogglePromotionStatus,
  getPromotionStatus,
} from "@/api-services/hooks/usePromotions";
import {
  Promotion,
  StatusOption,
  DiscountTypeOption,
  UserTypeOption,
  DISCOUNT_TYPES,
  USER_TYPES,
  STATUS_OPTIONS,
} from "@/api-services/hooks/promotionsTypes";
import DeletePromotionConfirmation from "@/components/promotions/DeletePromotionConfirmation";
import { formatGlobalCurrency, getStoredCurrency } from "@/api-services/hooks/useSettings";

const PromotionsPage = () => {
  const can = usePermissionCheck();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusOption>("all");
  const [discountTypeFilter, setDiscountTypeFilter] =
    useState<DiscountTypeOption>("all");
  const [userTypeFilter, setUserTypeFilter] = useState<UserTypeOption>("all");

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState<{
    id: number;
    details: string;
  } | null>(null);

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Handle search input change - just update search term, no page reset
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);


  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0); // Reset page along with debounced search
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Determine if we need client-side filtering
  const hasClientSideFilters = statusFilter !== "all" || discountTypeFilter !== "all" || userTypeFilter !== "all";
  
  // When client-side filters are active, fetch a large page size to have enough data to filter
  // Otherwise, use normal pagination
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage; // Always fetch from page 0 when filtering client-side

  // Reset to first page when search filter changes (client-side filters don't reset page)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length > 2;

  // Fetch promotions from API (only search filter goes to API, status/discount/user filters are client-side)
  const { data, isLoading: loading, error, refetch } = usePromotions(
    {
      page: apiPage,
      size: apiPageSize,
      ...(shouldSearch && { search: debouncedSearchTerm }),
    },
    {
      onError: (err) => {
        toast.error(`Failed to load promotions: ${err.message}`);
      },
    }
  );

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  // Delete promotion hook
  const { mutate: deletePromotion, isPending: isDeleting } = useDeletePromotion(
    {
      onSuccess: async (data, promotionId) => {
        toast.success("Promotion deleted successfully!");
        // Invalidate all promotions queries to ensure fresh data
        await queryClient.invalidateQueries({ queryKey: ['promotions'] });
      },
      onError: (error, promotionId) => {
        toast.error(`Failed to delete promotion: ${error.message}`);
      },
    }
  );

  // Toggle promotion status hook
  const { mutate: toggleStatus, isPending: isToggling } =
    useTogglePromotionStatus({
      onSuccess: async (data, promotionId) => {
        toast.success("Promotion status updated successfully!");
        // Update only the changed row in the cache instead of refetching entire table
        queryClient.setQueriesData(
          { queryKey: ['promotions'] },
          (oldData: any) => {
            if (!oldData?.content) return oldData;

            const updatedContent = oldData.content.map((promotion: any) =>
              promotion.promotionId === promotionId
                ? { ...promotion, isActive: !promotion.isActive }
                : promotion
            );

            return {
              ...oldData,
              content: updatedContent,
            };
          }
        );
      },
      onError: (error, promotionId) => {
        toast.error(`Failed to update promotion status: ${error.message}`);
      },
    });

  const getStatusBadge = useCallback((promotion: Promotion) => {
    const status = getPromotionStatus(promotion);

    switch (status) {
      case "active":
        return {
          variant: "default",
          className: "bg-green-100 text-green-800 border-green-200",
          label: "Active",
          icon: CheckCircle,
        };
      case "scheduled":
        return {
          variant: "secondary",
          className: "bg-blue-100 text-blue-800 border-blue-200",
          label: "Scheduled",
          icon: Clock,
        };
      case "expired":
        return {
          variant: "outline",
          className: "bg-gray-100 text-gray-800 border-gray-200",
          label: "Expired",
          icon: XCircle,
        };
      case "inactive":
        return {
          variant: "secondary",
          className: "bg-red-100 text-red-800 border-red-200",
          label: "Inactive",
          icon: AlertCircle,
        };
      default:
        return {
          variant: "secondary",
          className: "bg-muted text-muted-foreground",
          label: "Unknown",
          icon: AlertCircle,
        };
    }
  }, []);

  const getDiscountTypeBadge = useCallback((discountType: string) => {
    return discountType === "PERCENTAGE"
      ? {
          variant: "default",
          className: "bg-purple-100 text-purple-800 border-purple-200",
          label: "Percentage",
        }
      : {
          variant: "default",
          className: "bg-indigo-100 text-indigo-800 border-indigo-200",
          label: "Fixed Amount",
        };
  }, []);

  const handlePromotionAction = (
    action: string,
    promotionId: number,
    promotionDetails: string
  ) => {
    if (action === "Edit") {
      navigate(`/admin/promotions/edit/${promotionId}`);
    } else if (action === "Toggle Status") {
      toggleStatus(promotionId);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (promotionId: number, promotionDetails: string) => {
    setPromotionToDelete({
      id: promotionId,
      details: promotionDetails,
    });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = (promotionId: number) => {
    deletePromotion(promotionId);
    setDeleteConfirmationOpen(false);
    setPromotionToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setPromotionToDelete(null);
  };

  // Filter promotions by status, discount type, and user type on the client side
  const promotions = useMemo(() => {
    if (!data?.content) return [];

    let filtered = data.content;

    // Filter by status (active/scheduled/expired/inactive) using getPromotionStatus
    if (statusFilter !== "all") {
      filtered = filtered.filter((promotion) => {
        const status = getPromotionStatus(promotion);
        return status === statusFilter;
      });
    }

    // Filter by discount type
    if (discountTypeFilter !== "all") {
      filtered = filtered.filter((promotion) => {
        return promotion.discountType === discountTypeFilter;
      });
    }

    // Filter by user type
    if (userTypeFilter !== "all") {
      filtered = filtered.filter((promotion) => {
        return promotion.applicableUserType === userTypeFilter;
      });
    }

    return filtered;
  }, [data?.content, statusFilter, discountTypeFilter, userTypeFilter, hasClientSideFilters]);

  // Pagination calculations - use filtered count for client-side filters
  const totalFilteredItems = promotions.length;
  const totalItems = data?.page?.totalElements || 0;
  
  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (hasClientSideFilters) {
      // Client-side filtering: paginate filtered results
      return Math.ceil(totalFilteredItems / itemsPerPage);
    }
    // Server-side pagination: use API response
    return data?.page?.totalPages || 0;
  }, [totalFilteredItems, itemsPerPage, hasClientSideFilters, data?.page?.totalPages]);

  // Paginated promotions for current page (when using client-side filtering)
  const paginatedPromotions = useMemo(() => {
    if (!hasClientSideFilters) {
      // No client-side filtering, use API pagination
      return promotions;
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return promotions.slice(startIndex, endIndex);
  }, [promotions, currentPage, itemsPerPage, hasClientSideFilters]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Check if current page is empty after data loads and navigate to previous page if needed
  useEffect(() => {
    if (!loading && data && paginatedPromotions.length === 0 && currentPage > 0 && totalPages > 0) {
      // Current page is empty, navigate to the last valid page
      const lastPage = Math.max(0, totalPages - 1);
      setCurrentPage(lastPage);
    }
  }, [loading, paginatedPromotions.length, currentPage, totalPages, data]);

  // Format promotion details for display
  const getPromotionDetails = useCallback((promotion: Promotion) => {
    return `${promotion.promoCode} - ${
      promotion.city?.cityName || "Unknown City"
    }`;
  }, []);

  // Format currency display
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: getStoredCurrency(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);


  // Get discount display value
  const getDiscountDisplay = useCallback(
    (promotion: Promotion) => {
      if (promotion.discountType === "PERCENTAGE") {
        return `${Number(promotion.discountValue).toFixed(2)}%`;
      } else {
        return formatGlobalCurrency(promotion.discountValue);
      }
    },
    [formatGlobalCurrency]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading promotions data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <TrendingDown className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Promotions
            </h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Promotions Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage promotional codes, discount offers, and special deals
          </p>
        </div>
        <div className="flex gap-2">
          {can('Create Promotions') && (
            <Button
              className="bg-gradient-primary hover:opacity-90"
              onClick={() => navigate("/admin/promotions/add")}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Promotion
            </Button>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search promotions by code or description... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                autoComplete="off"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value: StatusOption) => setStatusFilter(value)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={discountTypeFilter}
              onValueChange={(value: DiscountTypeOption) =>
                setDiscountTypeFilter(value)
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {DISCOUNT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={userTypeFilter}
              onValueChange={(value: UserTypeOption) =>
                setUserTypeFilter(value)
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show All</SelectItem>
                {USER_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Promotions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Promotions (
            {hasClientSideFilters 
              ? `${totalFilteredItems} filtered` 
              : totalItems}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Promotion Details</TableHead>
                  <TableHead>Location & Vehicle</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Validity Period</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPromotions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {promotions.length === 0 
                        ? "No promotions found"
                        : "No promotions match the current filters"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPromotions.map((promotion) => {
                    const statusBadge = getStatusBadge(promotion);
                    const discountTypeBadge = getDiscountTypeBadge(
                      promotion.discountType
                    );
                    const StatusIcon = statusBadge.icon;
                    const promotionDetails = getPromotionDetails(promotion);

                    return (
                      <TableRow
                        key={promotion.promoCodeId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-muted-foreground" />
                              <span className="font-semibold text-lg">
                                {promotion.promoCode}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              {promotion.description}
                            </div>
                            {/* <div className="flex items-center gap-2">
                              <Users className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                {promotion.applicableUserType.replace("_", " ")}
                              </span>
                            </div> */}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">
                                {promotion.city?.cityName || "All Cities"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Car className="w-3 h-3" />
                              <span>
                                Vehicle ID: {promotion.vehicleCategoryId}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            <Badge className={discountTypeBadge.className}>
                              {discountTypeBadge.label}
                            </Badge>
                            <div className="font-bold text-lg text-green-600">
                              {getDiscountDisplay(promotion)}
                            </div>
                            { promotion.discountType === "PERCENTAGE" && promotion.maxDiscountAmount > 0 &&
                              (
                                <div className="text-xs text-muted-foreground">
                                  Max:{" "} {formatGlobalCurrency(promotion.maxDiscountAmount)}
                                </div>
                              )   
                            }                           
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              <span>{formatDisplayDate(promotion.validFrom)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              <span>{formatDisplayDate(promotion.validTo)}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            {/* <div className="font-medium">
                              {promotion.totalRedemptions} used
                            </div> */}
                            <div className="font-medium">
                              Max {promotion.maxUsesPerUser} per user
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {can('Edit Promotions') && (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    handlePromotionAction(
                                      "Edit",
                                      promotion.promoCodeId || 0,
                                      promotionDetails
                                    )
                                  }
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Promotion
                                </DropdownMenuItem>
                              )}
                              {can('Edit Promotions') && (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    handlePromotionAction(
                                      "Toggle Status",
                                      promotion.promoCodeId || 0,
                                      promotionDetails
                                    )
                                  }
                                  disabled={isToggling}
                                >
                                  {promotion.isActive ? (
                                    <>
                                      <ToggleLeft className="w-4 h-4 mr-2" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <ToggleRight className="w-4 h-4 mr-2" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {can('Delete Promotions') && (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    handleDeleteClick(
                                      promotion.promoCodeId || 0,
                                      promotionDetails
                                    )
                                  }
                                  className="text-destructive"
                                  disabled={isDeleting}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Promotion
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination - Always show if totalPages > 0, even if current page is empty */}
            {totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={hasClientSideFilters ? totalFilteredItems : totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Promotion Confirmation Modal */}
      <DeletePromotionConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        promotionDetails={promotionToDelete?.details || ""}
        promotionId={promotionToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PromotionsPage;
